// Global toast utility that can be imported anywhere, including Redux slices

export type ToastType = "success" | "error" | "warning" | "info";

export type ToastContent =
  | string
  | {
      title?: string;
      message: string;
    };

export const DEFAULT_TOAST_DURATION = 5000;

export const DEFAULT_TOAST_TITLES: Record<ToastType, string> = {
  success: "Success",
  error: "Error",
  warning: "Warning",
  info: "Information",
};

export function normalizeToastContent(
  content: ToastContent,
  type: ToastType
): { title: string; message: string } {
  if (typeof content === "string") {
    return {
      title: DEFAULT_TOAST_TITLES[type],
      message: content,
    };
  }

  return {
    title: content.title || DEFAULT_TOAST_TITLES[type],
    message: content.message,
  };
}

interface ToastFunctions {
  success: (content: ToastContent, duration?: number) => number;
  error: (content: ToastContent, duration?: number) => number;
  warning: (content: ToastContent, duration?: number) => number;
  info: (content: ToastContent, duration?: number) => number;
}

class ToastManager {
  private toastFunctions: ToastFunctions | null = null;

  setToastFunctions(functions: ToastFunctions) {
    this.toastFunctions = functions;
  }

  success(content: ToastContent, duration?: number): number {
    if (!this.toastFunctions) {
      console.warn(
        "Toast not initialized. Make sure ToastProvider is mounted."
      );
      return 0;
    }
    return this.toastFunctions.success(content, duration);
  }

  error(content: ToastContent, duration?: number): number {
    if (!this.toastFunctions) {
      console.warn(
        "Toast not initialized. Make sure ToastProvider is mounted."
      );
      return 0;
    }
    return this.toastFunctions.error(content, duration);
  }

  warning(content: ToastContent, duration?: number): number {
    if (!this.toastFunctions) {
      console.warn(
        "Toast not initialized. Make sure ToastProvider is mounted."
      );
      return 0;
    }
    return this.toastFunctions.warning(content, duration);
  }

  info(content: ToastContent, duration?: number): number {
    if (!this.toastFunctions) {
      console.warn(
        "Toast not initialized. Make sure ToastProvider is mounted."
      );
      return 0;
    }
    return this.toastFunctions.info(content, duration);
  }
}

// Export singleton instance
const toastManager = new ToastManager();

// Export convenience functions that can be imported in Redux slices
export const toast = {
  success: (content: ToastContent, duration?: number) =>
    toastManager.success(content, duration),
  error: (content: ToastContent, duration?: number) =>
    toastManager.error(content, duration),
  warning: (content: ToastContent, duration?: number) =>
    toastManager.warning(content, duration),
  info: (content: ToastContent, duration?: number) =>
    toastManager.info(content, duration),
};

// Export the manager for initialization
export { toastManager };
